import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./AddOrganization.css";
import OrganizationTabs from "./OrganizationTabs";

const organizationTypes = [
  { value: "BRANCH", label: "Branch" },
  { value: "DEPARTMENT", label: "Department" },
  { value: "OPERATING_UNIT", label: "Operating Unit" },
];

const allowedOrganizationTypes = [
  "BRANCH",
  "DEPARTMENT",
  "OPERATING_UNIT",
];

const emptyForm = {
  organization_type: "",
  name: "",
  legal_name: "",
  code: "",
  country_code: "",
  city: "",
  website: "",
  email: "",
  external_reference: "",
  metadata: "{}",
  is_active: true,
  parent: "",
  industry: "",
  owner: "",
  legal_entity: "",
};

const AddOrganization = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([
    { ...emptyForm },
  ]);

  const [tenantId, setTenantId] = useState(null);

  // ----------------------------------------------------------
  // GET TENANT ID
  // ----------------------------------------------------------

  useEffect(() => {
    const storedTenantUser = localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again.",
      });

      return;
    }

    try {
      const tenantUser = JSON.parse(storedTenantUser);

      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found.",
        });

        return;
      }

      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);

      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again.",
      });
    }
  }, []);

  // ----------------------------------------------------------
  // FETCH DATA
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) {
      fetchOrganizations();
      fetchIndustries();
    }
  }, [tenantId]);

  // ----------------------------------------------------------
  // FETCH ORGANIZATIONS
  // ONLY BRANCH / DEPARTMENT / OPERATING_UNIT
  // ----------------------------------------------------------

  const fetchOrganizations = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${API_BASE}/tenant-records/`,
        {
          tenant_id: Number(tenantId),
          tables: ["organizations"],
        }
      );

      console.log(
        "Tenant organizations response:",
        response.data
      );

      let data = response.data;

      if (response.data?.data) {
        data = response.data.data;
      }

      if (data?.organizations) {
        data = data.organizations;
      }

      if (!Array.isArray(data)) {
        data = [];
      }

      // ------------------------------------------------------
      // ONLY ALLOW INTERNAL ORGANIZATION TYPES
      // ------------------------------------------------------

      const filteredOrganizations = data.filter((organization) =>
        allowedOrganizationTypes.includes(
          organization.organization_type
        )
      );

      setOrganizations(filteredOrganizations);
    } catch (error) {
      console.error(
        "Error fetching organizations:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to load organizations",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Something went wrong while loading organizations.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // FETCH INDUSTRIES
  // ----------------------------------------------------------

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/reference-values/`
      );

      let data = response.data;

      if (response.data?.data) {
        data = response.data.data;
      }

      if (!Array.isArray(data)) {
        data = [];
      }

      const industryValues = data
        .filter(
          (item) =>
            item.option_set_value?.option_type === "INDUSTRY" &&
            item.is_active
        )
        .map((item) => ({
          id: item.id,
          code: item.code,
          label: item.label,
        }));

      setIndustries(industryValues);
    } catch (error) {
      console.error(
        "Error fetching industries:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to load industries",
        text: "Industry values could not be loaded.",
      });
    }
  };

  // ----------------------------------------------------------
  // HANDLE FORM CHANGE
  // ----------------------------------------------------------

  const handleChange = (index, e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) =>
      prev.map((organization, organizationIndex) =>
        organizationIndex === index
          ? {
              ...organization,
              [name]:
                type === "checkbox"
                  ? checked
                  : value,
            }
          : organization
      )
    );
  };

  // ----------------------------------------------------------
  // ADD ORGANIZATION
  // ----------------------------------------------------------

  const handleAdd = () => {
    setFormData([
      {
        ...emptyForm,
        metadata: "{}",
        is_active: true,
      },
    ]);

    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // ADD ANOTHER ORGANIZATION
  // ----------------------------------------------------------

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      {
        ...emptyForm,
        metadata: "{}",
        is_active: true,
      },
    ]);
  };

  // ----------------------------------------------------------
  // REMOVE ORGANIZATION FORM
  // ----------------------------------------------------------

  const handleRemoveOrganizationForm = (index) => {
    setFormData((prev) =>
      prev.filter(
        (_, organizationIndex) =>
          organizationIndex !== index
      )
    );
  };

  // ----------------------------------------------------------
  // EDIT ORGANIZATION
  // ----------------------------------------------------------

  const handleEdit = (organization) => {
    setFormData([
      {
        organization_type:
          organization.organization_type || "",
        name:
          organization.name || "",
        legal_name:
          organization.legal_name || "",
        code:
          organization.code || "",
        country_code:
          organization.country_code || "",
        city:
          organization.city || "",
        website:
          organization.website || "",
        email:
          organization.email || "",
        external_reference:
          organization.external_reference || "",
        metadata:
          typeof organization.metadata === "object"
            ? JSON.stringify(
                organization.metadata,
                null,
                2
              )
            : organization.metadata || "{}",
        is_active:
          organization.is_active !== undefined
            ? organization.is_active
            : true,
        parent:
          organization.parent || "",
        industry:
          organization.industry || "",
        owner:
          organization.owner || "",
        legal_entity:
          organization.legal_entity || "",
      },
    ]);

    setEditingId(organization.id);
    setIsEditing(true);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // DELETE ORGANIZATION
  // ----------------------------------------------------------

  const handleDelete = async (organization) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Organization?",
      text: `Are you sure you want to delete "${organization.name}"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE}/organizations/${organization.id}/`
      );

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Organization deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchOrganizations();
    } catch (error) {
      console.error(
        "Error deleting organization:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the organization.",
      });
    }
  };

  // ----------------------------------------------------------
  // SUBMIT FORM
  // ----------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant.",
      });

      return;
    }

    // --------------------------------------------------------
    // VALIDATE ALL ORGANIZATION FORMS
    // --------------------------------------------------------

    for (let i = 0; i < formData.length; i++) {
      const organization = formData[i];

      if (
        !allowedOrganizationTypes.includes(
          organization.organization_type
        )
      ) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Organization Type",
          text: `Please select Branch, Department, or Operating Unit for Organization ${
            i + 1
          }.`,
        });

        return;
      }

      if (!organization.name) {
        Swal.fire({
          icon: "warning",
          title: "Organization Name Required",
          text: `Please enter Organization Name for Organization ${
            i + 1
          }.`,
        });

        return;
      }

      // Owner is mandatory for all three types
      if (!organization.owner) {
        Swal.fire({
          icon: "warning",
          title: "Owner Required",
          text: `Please enter Owner for Organization ${
            i + 1
          }.`,
        });

        return;
      }

      try {
        if (organization.metadata) {
          JSON.parse(organization.metadata);
        }
      } catch (error) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Metadata",
          text: `Please enter valid JSON in the metadata field for Organization ${
            i + 1
          }.`,
        });

        return;
      }
    }

    try {
      setLoading(true);

      // ------------------------------------------------------
      // EDIT
      // ------------------------------------------------------

      if (isEditing) {
        const organization = formData[0];

        const parsedMetadata = organization.metadata
          ? JSON.parse(organization.metadata)
          : {};

        const payload = {
          organization_type:
            organization.organization_type,

          name:
            organization.name,

          legal_name:
            organization.legal_name,

          code:
            organization.code,

          country_code:
            organization.country_code,

          city:
            organization.city,

          website:
            organization.website || null,

          email:
            organization.email || null,

          external_reference:
            organization.external_reference,

          metadata:
            parsedMetadata,

          is_active:
            organization.is_active,

          tenant:
            Number(tenantId),

          parent:
            organization.parent
              ? Number(organization.parent)
              : null,

          industry:
            organization.industry
              ? Number(organization.industry)
              : null,

          owner:
            organization.owner
              ? Number(organization.owner)
              : null,

          legal_entity:
            organization.legal_entity
              ? Number(organization.legal_entity)
              : null,
        };

        await axios.put(
          `${API_BASE}/organizations/${editingId}/`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title: "Organization Updated",
          text: "Organization updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // ------------------------------------------------------
      // CREATE MULTIPLE ORGANIZATIONS
      // ------------------------------------------------------

      else {
        for (const organization of formData) {
          const parsedMetadata =
            organization.metadata
              ? JSON.parse(
                  organization.metadata
                )
              : {};

          const payload = {
            organization_type:
              organization.organization_type,

            name:
              organization.name,

            legal_name:
              organization.legal_name,

            code:
              organization.code,

            country_code:
              organization.country_code,

            city:
              organization.city,

            website:
              organization.website || null,

            email:
              organization.email || null,

            external_reference:
              organization.external_reference,

            metadata:
              parsedMetadata,

            is_active:
              organization.is_active,

            tenant:
              Number(tenantId),

            parent:
              organization.parent
                ? Number(organization.parent)
                : null,

            industry:
              organization.industry
                ? Number(organization.industry)
                : null,

            owner:
              organization.owner
                ? Number(organization.owner)
                : null,

            legal_entity:
              organization.legal_entity
                ? Number(
                    organization.legal_entity
                  )
                : null,
          };

          await axios.post(
            `${API_BASE}/organizations/`,
            payload
          );
        }

        await Swal.fire({
          icon: "success",
          title: "Organizations Created",
          text: `${formData.length} organization${
            formData.length > 1
              ? "s"
              : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyForm }]);

      await fetchOrganizations();
    } catch (error) {
      console.error(
        "Error saving organizations:",
        error
      );

      const backendError =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to save organizations.";

      Swal.fire({
        icon: "error",
        title: isEditing
          ? "Update failed"
          : "Creation failed",
        text:
          typeof backendError === "string"
            ? backendError
            : JSON.stringify(
                backendError
              ),
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // CANCEL
  // ----------------------------------------------------------

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyForm }]);
  };

  // ----------------------------------------------------------
  // ORGANIZATION TYPE LABEL
  // ----------------------------------------------------------

  const getOrganizationTypeLabel = (type) => {
    return (
      organizationTypes.find(
        (item) =>
          item.value === type
      )?.label ||
      type ||
      "-"
    );
  };

  // ----------------------------------------------------------
  // INDUSTRY LABEL
  // ----------------------------------------------------------

  const getIndustryLabel = (industryId) => {
    if (!industryId) {
      return "-";
    }

    const industry = industries.find(
      (item) =>
        Number(item.id) ===
        Number(industryId)
    );

    return industry
      ? `${industry.code} - ${industry.label}`
      : `Industry #${industryId}`;
  };

  // ----------------------------------------------------------
  // PARENT NAME
  // ----------------------------------------------------------

  const getParentName = (parentId) => {
    if (!parentId) {
      return "-";
    }

    const parent = organizations.find(
      (organization) =>
        Number(organization.id) ===
        Number(parentId)
    );

    return (
      parent?.name ||
      `Organization #${parentId}`
    );
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="organization-page">
          <OrganizationTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="organization-page-header">
                <div>
                  <h2>Organizations</h2>
                </div>

                <button
                  type="button"
                  className="organization-add-btn"
                  onClick={handleAdd}
                >
                  + Add Organization
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="organization-list-card">
                {loading ? (
                  <div className="organization-loading">
                    Loading organizations...
                  </div>
                ) : organizations.length === 0 ? (
                  <div className="organization-empty">
                    <div className="organization-empty-icon">
                      +
                    </div>

                    <h3>
                      No Organizations Found
                    </h3>

                    <p>
                      There are no Branches,
                      Departments, or Operating
                      Units available for the
                      current tenant.
                    </p>

                    <button
                      type="button"
                      className="organization-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Organization
                    </button>
                  </div>
                ) : (
                  <div className="organization-table-wrapper">
                    <table className="organization-table">
                      <thead>
                        <tr>
                          <th>Organization</th>
                          <th>Type</th>
                          <th>Code</th>
                          <th>Industry</th>
                          <th>Location</th>
                          <th>Parent</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {organizations.map(
                          (organization) => (
                            <tr
                              key={
                                organization.id
                              }
                            >
                              <td>
                                <div className="organization-name-cell">
                                  <div className="organization-avatar">
                                    {organization.name
                                      ?.charAt(0)
                                      ?.toUpperCase() ||
                                      "O"}
                                  </div>

                                  <div>
                                    <strong>
                                      {organization.name ||
                                        "-"}
                                    </strong>

                                    <span>
                                      {organization.legal_name ||
                                        "No legal name"}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <span className="organization-type-pill">
                                  {getOrganizationTypeLabel(
                                    organization.organization_type
                                  )}
                                </span>
                              </td>

                              <td>
                                {organization.code ||
                                  "-"}
                              </td>

                              <td>
                                {getIndustryLabel(
                                  organization.industry
                                )}
                              </td>

                              <td>
                                <div className="organization-location">
                                  <strong>
                                    {organization.city ||
                                      "-"}
                                  </strong>

                                  {organization.country_code && (
                                    <span>
                                      {
                                        organization.country_code
                                      }
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td>
                                {getParentName(
                                  organization.parent
                                )}
                              </td>

                              <td>
                                <span
                                  className={`organization-status ${
                                    organization.is_active
                                      ? "active"
                                      : "inactive"
                                  }`}
                                >
                                  {organization.is_active
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              </td>

                              <td>
                                <div className="organization-actions">
                                  <button
                                    type="button"
                                    className="organization-edit-btn"
                                    onClick={() =>
                                      handleEdit(
                                        organization
                                      )
                                    }
                                  >
                                    Edit
                                  </button>

                                  <button
                                    type="button"
                                    className="organization-delete-btn"
                                    onClick={() =>
                                      handleDelete(
                                        organization
                                      )
                                    }
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="organization-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Organization"
                      : "Create Organizations"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="organization-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map(
                  (
                    organization,
                    index
                  ) => (
                    <div
                      className="organization-card"
                      key={index}
                      style={{
                        marginBottom: "20px",
                      }}
                    >
                      <div className="organization-form-body">
                        {/* ==================================================
                            FORM TITLE
                        ================================================== */}

                        <div
                          className="organization-section-title"
                          style={{
                            display: "flex",
                            justifyContent:
                              "space-between",
                            alignItems:
                              "center",
                          }}
                        >
                          <span>
                            Organization{" "}
                            {index + 1}
                          </span>

                          {!isEditing &&
                            formData.length >
                              1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveOrganizationForm(
                                    index
                                  )
                                }
                                style={{
                                  border: "none",
                                  background:
                                    "transparent",
                                  color:
                                    "#dc2626",
                                  cursor:
                                    "pointer",
                                  fontSize:
                                    "13px",
                                  fontWeight:
                                    "600",
                                }}
                              >
                                Remove
                              </button>
                            )}
                        </div>

                        {/* ==================================================
                            BASIC INFORMATION
                        ================================================== */}

                        <div className="organization-section-title">
                          Basic Information
                        </div>

                        <div className="organization-form-grid">
                          {/* ORGANIZATION TYPE */}

                          <div className="organization-form-group">
                            <label>
                              Organization
                              Type{" "}
                              <span>
                                *
                              </span>
                            </label>

                            <select
                              name="organization_type"
                              value={
                                organization.organization_type
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              required
                            >
                              <option value="">
                                Select
                                organization
                                type
                              </option>

                              {organizationTypes.map(
                                (type) => (
                                  <option
                                    key={
                                      type.value
                                    }
                                    value={
                                      type.value
                                    }
                                  >
                                    {
                                      type.label
                                    }
                                  </option>
                                )
                              )}
                            </select>
                          </div>

                          {/* ORGANIZATION NAME */}

                          <div className="organization-form-group">
                            <label>
                              Organization
                              Name{" "}
                              <span>
                                *
                              </span>
                            </label>

                            <input
                              type="text"
                              name="name"
                              value={
                                organization.name
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter organization name"
                              maxLength={200}
                              required
                            />
                          </div>

                          {/* LEGAL NAME */}

                          <div className="organization-form-group">
                            <label>
                              Legal Name
                            </label>

                            <input
                              type="text"
                              name="legal_name"
                              value={
                                organization.legal_name
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter legal name"
                              maxLength={250}
                            />
                          </div>

                          {/* ORGANIZATION CODE */}

                          <div className="organization-form-group">
                            <label>
                              Organization
                              Code
                            </label>

                            <input
                              type="text"
                              name="code"
                              value={
                                organization.code
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter short code"
                              maxLength={60}
                            />
                          </div>

                          {/* INDUSTRY */}

                          <div className="organization-form-group">
                            <label>
                              Industry
                            </label>

                            <select
                              name="industry"
                              value={
                                organization.industry
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                            >
                              <option value="">
                                Select
                                industry
                              </option>

                              {industries.map(
                                (industry) => (
                                  <option
                                    key={
                                      industry.id
                                    }
                                    value={
                                      industry.id
                                    }
                                  >
                                    {
                                      industry.code
                                    }{" "}
                                    -{" "}
                                    {
                                      industry.label
                                    }
                                  </option>
                                )
                              )}
                            </select>
                          </div>

                          {/* COUNTRY CODE */}

                          <div className="organization-form-group">
                            <label>
                              Country
                              Code
                            </label>

                            <input
                              type="text"
                              name="country_code"
                              value={
                                organization.country_code
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="e.g. IN"
                              maxLength={2}
                            />
                          </div>

                          {/* CITY */}

                          <div className="organization-form-group">
                            <label>
                              City
                            </label>

                            <input
                              type="text"
                              name="city"
                              value={
                                organization.city
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter city"
                              maxLength={120}
                            />
                          </div>

                          {/* WEBSITE */}

                          <div className="organization-form-group">
                            <label>
                              Website
                            </label>

                            <input
                              type="url"
                              name="website"
                              value={
                                organization.website
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="https://example.com"
                            />
                          </div>

                          {/* EMAIL */}

                          <div className="organization-form-group">
                            <label>
                              Email
                            </label>

                            <input
                              type="email"
                              name="email"
                              value={
                                organization.email
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter organization email"
                            />
                          </div>

                          {/* EXTERNAL REFERENCE */}

                          <div className="organization-form-group">
                            <label>
                              External
                              Reference
                            </label>

                            <input
                              type="text"
                              name="external_reference"
                              value={
                                organization.external_reference
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter external reference"
                              maxLength={100}
                            />
                          </div>

                          {/* PARENT */}

                          <div className="organization-form-group">
                            <label>
                              Parent
                              Organization
                            </label>

                            <select
                              name="parent"
                              value={
                                organization.parent
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                            >
                              <option value="">
                                No parent
                                organization
                              </option>

                              {organizations
                                .filter(
                                  (
                                    existingOrganization
                                  ) =>
                                    Number(
                                      existingOrganization.id
                                    ) !==
                                    Number(
                                      editingId
                                    )
                                )
                                .map(
                                  (
                                    existingOrganization
                                  ) => (
                                    <option
                                      key={
                                        existingOrganization.id
                                      }
                                      value={
                                        existingOrganization.id
                                      }
                                    >
                                      {
                                        existingOrganization.name
                                      }
                                    </option>
                                  )
                                )}
                            </select>
                          </div>

                          {/* STATUS */}

                          <div className="organization-status-field">
                            <label>
                              Organization
                              Status
                            </label>

                            <label className="organization-switch">
                              <input
                                type="checkbox"
                                name="is_active"
                                checked={
                                  organization.is_active
                                }
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    e
                                  )
                                }
                              />

                              <span className="organization-slider"></span>
                            </label>

                            <span className="organization-status-text">
                              {organization.is_active
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </div>
                        </div>


                        <div className="organization-form-grid">
                          {/* OWNER */}

                          <div className="organization-form-group">
                            <label>
                              Owner{" "}
                              <span>
                                *
                              </span>
                            </label>

                            <input
                              type="number"
                              name="owner"
                              value={
                                organization.owner
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter owner user ID"
                              required
                            />
                          </div>

                          {/* LEGAL ENTITY */}

                          <div className="organization-form-group">
                            <label>
                              Legal Entity
                            </label>

                            <input
                              type="number"
                              name="legal_entity"
                              value={
                                organization.legal_entity
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter legal entity ID"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* ==================================================
                    ADD ANOTHER ORGANIZATION
                ================================================== */}

                {!isEditing && (
                  <button
                    type="button"
                    className="organization-add-btn"
                    onClick={
                      handleAddAnother
                    }
                    style={{
                      marginBottom: "20px",
                    }}
                  >
                    + Add Another
                    Organization
                  </button>
                )}

                {/* ==================================================
                    FORM ACTIONS
                ================================================== */}

                <div className="organization-form-actions">
                  <button
                    type="button"
                    className="organization-cancel-btn"
                    onClick={
                      handleCancel
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="organization-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Organization"
                      : "Save Organizations"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddOrganization;